/**
 * @fileoverview Input/output schemas for next-chapter-suggester-v2.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.seriesContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterMetadata.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.actualLastThreeChaptersContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.latestKnownPlotState.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  targetChapterNumber: z.number().describe("The chapter number these suggestions are for (the next unwritten chapter)."),
  suggestions: z.array(z.object({
    title: z.string().describe("A short, compelling title for this chapter idea (e.g., 'A Shocking Betrayal', 'An Unexpected Ally')."),
    description: z.string().describe("A paragraph describing the suggested plot, character, and narrative developments for this single chapter."),
    justification: z.string().describe('A brief explanation of why this chapter direction is a good fit, considering pacing, plot progression, and character arcs.'),
  })).length(3).describe('An array of exactly three distinct suggestions for the next chapter.'),
});

export type Output = z.infer<typeof OutputSchema>;
